/**
 * PATIENT-CRM-TIMELINE-01 — pure helpers + wire-contract mirror for the
 * read-only patient 360 timeline (`/dashboard/patients/:patientId/timeline`).
 *
 * Pure by construction — no React, no fetch, no Supabase, no `Date.now()`
 * (callers inject any time) — the patient-master-view.ts / leads-workspace-
 * view.ts doctrine, so every helper unit-tests without jsdom.
 *
 * SECURITY POSTURE: nothing here is an access decision. The timeline route
 * is gated by `RequirePatientMaster` (the server-computed
 * `me.patientMasterEnabled` signal, absent = disabled, fail-closed) and the
 * backend route auth remains the security boundary. With the flag off the
 * guard redirects, so the page never mounts and never fetches.
 *
 * CONTRACT MIRRORS (separate-packages convention, kept in lockstep by
 * review): the wire types below mirror the PHI-free PatientTimeline contract
 * in lib/domain/patient/patient-timeline.ts (produced by
 * api/routes/patient-master-timeline.ts). PHI-ZERO: opaque ids, UMR,
 * controlled labels, status tokens, ISO timestamps, clinic labels, counts,
 * and an opaque crossmatch verdict — never a name / phone / dob / email /
 * fingerprint.
 *
 * DISPLAY-ONLY: this surface renders a story. It exposes NO action that
 * links, merges, or creates (that is the patient-master merge state
 * machine, a separate gated surface).
 */

import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';

// ─── Wire contract mirrors (lib/domain/patient/patient-timeline.ts) ──────

export type PatientTimelineLane =
  | 'origin'
  | 'lifecycle'
  | 'appointment'
  | 'walkin'
  | 'link';

/** Mirrors lib/domain/patient/walkin-link.ts WalkinPatientLinkStatus. */
export type WalkinPatientLinkStatus =
  | 'linked'
  | 'candidate'
  | 'not_connected'
  | 'unavailable';

export type AppointmentCanonicalStatus =
  | 'waitlist'
  | 'booked'
  | 'arrived'
  | 'checked_in'
  | 'fulfilled'
  | 'cancelled'
  | 'no_show';

export type CrossmatchVerdict =
  | 'no_match'
  | 'possible_match'
  | 'strong_match'
  | 'not_available';

export interface AppointmentObservation {
  readonly storedStatus: string;
  readonly storedLabel: string;
  readonly canonicalStatus: AppointmentCanonicalStatus | null;
  readonly canonicalLabel: string | null;
  readonly occurredAt: string;
  readonly provenance: string;
  /** F-1 (PATIENT-360-AND-JOURNEY-TRUTH-CORRECTIVE-V1): the appointments.id
   *  when this observation is a DIRECT appointments-table read; absent for
   *  the older lead-event projection. */
  readonly appointmentId?: number | null;
  /** F-1: 'direct' = appointments.patient_id; 'lead_linked' = via the lead
   *  bridge (patient_id null); 'lead_event' = the lead-event projection.
   *  Absent = lead_event. */
  readonly linkage?: 'direct' | 'lead_linked' | 'lead_event';
}

export interface PatientTimelineEntry {
  readonly lane: PatientTimelineLane;
  readonly occurredAt: string;
  readonly eventType: string | null;
  readonly label: string;
  readonly summary: string;
  readonly stage: string | null;
  readonly appointment: AppointmentObservation | null;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly provenance: string;
}

export interface PatientTimelineResponse {
  readonly patientId: string;
  readonly umr: string;
  readonly status: string;
  readonly registeredClinicId: number | null;
  readonly registeredClinicLabel: string | null;
  readonly currentStage: string | null;
  readonly linkedLeadIds: readonly string[];
  readonly crossmatch: {
    readonly verdict: CrossmatchVerdict;
    readonly candidateCount: number;
  } | null;
  readonly appointmentCurrent: AppointmentCanonicalStatus | null;
  readonly appointmentHistory: readonly AppointmentObservation[];
  readonly entries: readonly PatientTimelineEntry[];
  readonly eventStreamStatus: 'available' | 'not_wired';
  /**
   * Confirmed walk-in linkage. 'linked' = >=1 walk-in carries an explicit,
   * human-confirmed link to this patient (those rows render on the `walkin`
   * lane in `entries`); 'not_connected' = none. The read-model never emits a
   * fingerprint-derived walk-in.
   */
  readonly walkinLinkStatus: WalkinPatientLinkStatus;
  readonly provenance: { readonly system: string; readonly caveat: string };
}

// ─── Display helpers (pure) ──────────────────────────────────────────────

const LANE_LABELS: Readonly<Record<PatientTimelineLane, string>> = {
  origin: 'Origin',
  lifecycle: 'Lifecycle',
  appointment: 'Appointment',
  walkin: 'Walk-in',
  link: 'Patient master',
};

/** Short, PHI-free lane label for the timeline rail. */
export function laneLabel(lane: PatientTimelineLane): string {
  return LANE_LABELS[lane] ?? lane;
}

/**
 * PHI-free display label for a lifecycle stage token (mirrors the backend
 * six-part contract outcomes in lib/domain/lead/lead-lifecycle.ts). Unknown
 * tokens pass through unchanged (forward-compatible with additive stages).
 */
const STAGE_LABELS: Readonly<Record<string, string>> = {
  new: 'New lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  appointment_booked: 'Appointment booked',
  arrived: 'Arrived',
  patient: 'Patient',
  converted_to_revenue: 'Converted to revenue',
  lost: 'Lost',
};

export function stageLabel(stage: string | null): string | null {
  if (stage === null) return null;
  return STAGE_LABELS[stage] ?? stage;
}

/**
 * Tailwind chip class for an appointment's canonical current-state — a
 * muted neutral for in-flight, success for fulfilled, destructive for the
 * negative branches. Display sugar only; never an access signal.
 */
export function appointmentChipClass(
  canonical: AppointmentCanonicalStatus | null,
): string {
  switch (canonical) {
    case 'fulfilled':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'no_show':
    case 'cancelled':
      return 'bg-destructive/10 text-destructive border-destructive/30';
    case 'booked':
    case 'arrived':
    case 'checked_in':
    case 'waitlist':
      return 'bg-muted/60 text-foreground border-border';
    default:
      return 'bg-muted/40 text-muted-foreground border-border';
  }
}

const VERDICT_LABELS: Readonly<Record<CrossmatchVerdict, string>> = {
  strong_match: 'Strong existing-patient match',
  possible_match: 'Possible existing-patient match',
  no_match: 'No existing-patient match',
  not_available: 'Crossmatch unavailable',
};

/**
 * Human caption for the advisory crossmatch verdict, or null when there is
 * nothing honest to show (`not_available` renders nothing — never a false
 * "no match"). The count is shown only for the match verdicts.
 */
export function crossmatchCaption(
  crossmatch: PatientTimelineResponse['crossmatch'],
): string | null {
  if (crossmatch === null) return null;
  if (crossmatch.verdict === 'not_available') return null;
  const base = VERDICT_LABELS[crossmatch.verdict];
  if (crossmatch.verdict === 'no_match') return base;
  return `${base} · ${crossmatch.candidateCount} candidate${
    crossmatch.candidateCount === 1 ? '' : 's'
  }`;
}

/**
 * F-1: a short honest linkage caption for an appointment observation. Direct
 * rows are "Linked to this patient"; lead-bridge rows are honestly "not
 * directly linked"; the lead-event projection (no appointment row) is the
 * historical supplement. Returns null when there is nothing to add (a plain
 * lead-event observation on a surface that does not badge it). PHI-free.
 */
export function appointmentLinkageLabel(
  linkage: AppointmentObservation['linkage'],
): string | null {
  switch (linkage) {
    case 'direct':
      return 'Linked to this patient';
    case 'lead_linked':
      return 'Via lead - not directly linked';
    default:
      return null;
  }
}

/**
 * ISO timestamp -> a human-readable clinic-local (IST) display string via THE
 * ONE shared formatter (CORE-DEMO-JOURNEY-STITCH-V1 F4: the journey page must
 * read like an enterprise record -- never a raw UTC string). Pure +
 * DETERMINISTIC (fixed +5:30, no `Date.now()` / locale), the same instrument
 * the record header, band, and Patient 360 render, so the timeline speaks the
 * same time vocabulary. A non-ISO value degrades to itself. PHI-free.
 */
export function timelineWhen(occurredAt: string): string {
  return formatTimestampIst(occurredAt);
}

/** The record-page href for the timeline's "back to record" link. */
export function patientRecordHref(patientId: string): string {
  return `/dashboard/patients/${encodeURIComponent(patientId)}`;
}

/** Whether the assembled timeline has any chronological entry to show. */
export function hasJourney(response: PatientTimelineResponse | null): boolean {
  return response !== null && response.entries.length > 0;
}

/** Count of confirmed walk-in lane entries in the assembled timeline. */
export function walkinEntryCount(
  response: PatientTimelineResponse | null,
): number {
  if (response === null) return 0;
  return response.entries.filter((e) => e.lane === 'walkin').length;
}

/**
 * PHI-free caption for the walk-in lane. When confirmed walk-ins are linked,
 * a short count line; otherwise the honest "not yet patient-linked" note.
 * Display copy only — never an action, never PHI.
 */
export function walkinLinkCaption(
  status: WalkinPatientLinkStatus,
  linkedCount: number,
): string {
  if (status === 'linked') {
    return `${linkedCount} confirmed walk-in${
      linkedCount === 1 ? '' : 's'
    } linked to this patient.`;
  }
  return 'Walk-in activity is not yet patient-linked.';
}
